import React, { useEffect, useState, useRef } from "react";
import DatePicker from "react-datepicker";
import { useLocation, useNavigate, Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import "./ResidentialRental.css";
import Cookies from "js-cookie"; // Import js-cookie if not already imported
import SimpleModal from "./SimpleModal";
import Loader from "./Loader"; // Import a loader component
import PropertyCard from "./PropertyCard";
import NewBuilderCard from "./NewBuilderCard"; // Import NewBuilderCard for builder route
import BuilderCard from "./BuilderCard"; // Import BuilderCard for builder details
import "react-datepicker/dist/react-datepicker.css"; // Importing DatePicker styles
import { debounce, max } from "lodash"; // Make sure lodash is installed or implement your own debounce function
import List from "./List"; // Ensure this import is present
import ShareModal from "./ShareModal";

import { ArrowLeft, Minimize2, Maximize2, SlidersHorizontal, X, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from "lucide-react";
import apiClient from "../common/apiClient";
import { fetchAreas } from "../common/areasApi";
import { useUserProfile, DEFAULT_PROPERTY_CITIES } from "../contexts/UserProfileContext";
import { log } from "three/src/utils.js";

const ResidentialRental = () => {
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPageChanging, setIsPageChanging] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedListedBy, setSelectedListedBy] = useState("");
  const [selectedListedOn, setSelectedListedOn] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [dateRange, setDateRange] = useState(""); // Add state for date range dropdown
  const [searchQuery, setSearchQuery] = useState("");
  const [propertyTypes, setPropertyTypes] = useState([]); // State for property types
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768); // Add state for mobile detection
  const [familyFilterActive, setFamilyFilterActive] = useState(false); // ✅ Track if family filter is active
  const [contactInfoMap, setContactInfoMap] = useState({}); // State to manage contact info across all property cards
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState({
    bhks: [],
    minRent: "",
    maxRent: "",
    minsqFt: "",
    maxsqFt: "",
    furnishedTypes: [],
    areas: [],
    subType: [],
    city: localStorage.getItem("selectedCity") || "Ahmedabad West",
  });

  const { hasCityAccess, propertyAccessCities } = useUserProfile();

  // Driven by PropertyAccessCity: only the cities the user actually has access to
  // are rendered. Falls back to the default list until the profile resolves.
  const cityOptions = propertyAccessCities.length ? propertyAccessCities : DEFAULT_PROPERTY_CITIES;

  const handleCitySelect = (city) => {
    setLocalFilters((prev) => ({ ...prev, city }));
    localStorage.setItem("selectedCity", city); // ✅ keep the app-wide city in sync
  };

  // If the selected city isn't in the user's PropertyAccessCity list, auto-switch
  // to the first accessible city (e.g. default "Ahmedabad West" -> "Ahmedabad East").
  useEffect(() => {
    if (propertyAccessCities.length === 0) return; // no restriction / not loaded yet
    if (hasCityAccess(localFilters.city)) return; // current selection already allowed
    const firstAllowed = cityOptions.find((c) => hasCityAccess(c));
    if (firstAllowed && firstAllowed !== localFilters.city) {
      handleCitySelect(firstAllowed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyAccessCities.join("|"), localFilters.city]);
  const [activeFilters, setActiveFilters] = useState(null); // Holds applied filters from the panel
  const [budgetExpanded, setBudgetExpanded] = useState(true);
  const [bhkExpanded, setBhkExpanded] = useState(true);
  const [sqftExpanded, setSqftExpanded] = useState(true);
  const [furnishedExpanded, setFurnishedExpanded] = useState(true);
  const [areaExpanded, setAreaExpanded] = useState(true);
  const [areaSearchQuery, setAreaSearchQuery] = useState("");
  const [areaOptions, setAreaOptions] = useState([]); // Loaded per city + section
  const [isFetchingAreas, setIsFetchingAreas] = useState(false);
  const areaSearchRef = useRef(null);
  const [subTypeExpanded, setSubTypeExpanded] = useState(true);
  const [subTypeSearchQuery, setSubTypeSearchQuery] = useState("");
  const [isFilterSidebarVisible, setIsFilterSidebarVisible] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();
  const { type: routeType } = useParams();

  // The route carries the section the user arrived from — /residential-rental
  // for owner, /residential-rental/<section> for the rest.
  const getModuleType = () => {
    if (routeType === "broker") return "broker";
    if (routeType === "builder") return "builder";
    if (routeType === "requirement") return "requirement";
    return "owner";
  };

  const {
    type: stateType,
    status,
    filters,
    scrollToTop,
    selectedListedOn: stateDate,
    selectedEndDate: stateEndDate,
    dateRange: stateDateRange,
  } = location.state || {};

  const [type, setType] = useState(stateType || "owner");

  const subTypes=[
    "Apartment",
    "Bungalow",
    "Tenement",
    "Penthouse",
    "Weekend Home",
    "PG",
    "Rowhouse",
    "Residential Plot",
    "Pre Leased Spaces",
    "Basement",
    "Commercial Plot",
    "Shed",
    "Co Working Space",
    "Factory",
    "Shop",
    "Commercial Building",
    "Godown",
    "Showroom",
    "Restaurant",
    "Industrial Land",
    "Commercial Flat",
    "Office",
    "Ware House",
    "Pre Leased Spaces",
    "Commericial Bunglow",
    "Land",
    "Agriculture Land",
    "PG House",
    "Hotel",
    "Gym",
    "Spa",
    "Hospital",
    "Salon",
    "Petrol Pump",
    "Cafe",
    "PreSchool",
    "Industrial Plot",
    "Industrial Shed",
  ]

  // Update initial states that depend on location.state
  useEffect(() => {
    if (status) setSelectedStatus(status);
    if (stateDate) {
      const date = stateDate instanceof Date ? stateDate : new Date(stateDate);
      if (!isNaN(date.getTime())) setSelectedListedOn(date);
    }
    if (stateEndDate) {
      const date = stateEndDate instanceof Date ? stateEndDate : new Date(stateEndDate);
      if (!isNaN(date.getTime())) setSelectedEndDate(date);
    }
    if (stateDateRange) setDateRange(stateDateRange);
  }, [status, stateDate, stateEndDate, stateDateRange]);

  // Reset scroll position when component mounts or when navigating to this page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isPremium = Cookies.get("isPremium");

  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    content: null,
  });

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.();
    }
  };

  // Keep the icon/state in sync even when fullscreen is exited natively
  // (Escape key, browser chrome button) instead of via our own button.
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const [isListView, setIsListView] = useState(() => {
    const savedView = localStorage.getItem("propertyListView");
    return window.innerWidth >= 768 && savedView === "list";
  });

  // Fetch property types from the server or define them here
  useEffect(() => {
    const fetchPropertyTypes = async () => {
      try {
        const response = await apiClient("get", "/property/types");
        // const response = await axios.get(`${process.env.REACT_APP_API_IP}/property/types`);
        setPropertyTypes(response.data.types);
      } catch (error) {
        // console.error("Error fetching property types:", error);
        // Fallback to predefined types if fetching fails
        setPropertyTypes([
          "All",
          "Residential Rent",
          "Commercial Rent",
          "Residential Sell",
          "Commercial Sell",
        ]);
      }
    };

    fetchPropertyTypes();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      setIsMobile(isMobile);
      if (isMobile) {
        setIsListView(false); // Force grid view on mobile
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSearch = () => {
    if (searchQuery.trim()) {
      // Split by comma and trim each premise name, filter out empty strings
      const premises = searchQuery
        .split(",")
        .map((premise) => premise.trim())
        .filter((premise) => premise.length > 0);

      // Call your API with the array of premise names
      fetchProperties(0, premises);
    } else {
      // If search query is empty, fetch all properties with current filters
      fetchProperties(0);
    }
  };

  const handleDateRangeChange = (e) => {
    const value = e.target.value;
    setDateRange(value);

    const today = new Date();
    let startDate = null;
    let endDate = null;

    switch (value) {
      case "single":
        // Don't set any dates, let user pick a single date
        startDate = null;
        endDate = null;
        break;
      case "last7days":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 7);
        endDate = today;
        break;
      case "last15days":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 15);
        endDate = today;
        break;
      case "last30days":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 30);
        endDate = today;
        break;
      case "":
        // Clear all dates when no range is selected
        startDate = null;
        endDate = null;
        break;
      default:
        startDate = null;
        endDate = null;
    }

    setSelectedListedOn(startDate);
    setSelectedEndDate(endDate);
    // console.log("Date range changed:", value, "Start:", startDate, "End:", endDate);
  };

  const handleDateChange = (date) => {
    setSelectedListedOn(date);

    // If no date range is selected, clear the end date when selecting a new start date
    if (!dateRange) {
      setSelectedEndDate(null);
    } else if (dateRange === "single") {
      // For single date mode, set end date to same as start date
      setSelectedEndDate(date);
    }
    // For other date ranges (last7days, last15days, last30days),
    // the end date is already set by handleDateRangeChange

    // console.log("Start date selected:", date, "Date range:", dateRange);
  };

  const handleEndDateChange = (date) => {
    setSelectedEndDate(date);
    // console.log("End date selected:", date);
  };
  const normalizePrice = (price) => {
    if (!price) return 0;

    let s = price.toString().toLowerCase().trim();

    if (s.includes("lac")) return parseFloat(s) * 100000;
    if (s.includes("cr")) return parseFloat(s) * 10000000;

    return Number(s.replace(/[^0-9.]/g, "")) || 0;
  };

  const fetchProperties = async (
    page = 0,
    premises = [],
    selectedType = type
  ) => {
    setIsLoading(true);

    try {
      const userId = Cookies.get("userId");
      const token = Cookies.get("token");

      if (!token) {
        console.error("No authentication token found");
        return;
      }

      let payload = {};
      let endpoint = "";

      // Build date filters if a start date is selected
      let datePart = {};
      if (selectedListedOn) {
        const startDate = formatDate(selectedListedOn);
        const endDate = selectedEndDate
          ? formatDate(selectedEndDate)
          : startDate;
        datePart = { startDate, endDate };
      }

      // ✅ IMPROVED: Get areas from filters
      // Merge location state filters with activeFilters from the panel
      const mergedFilters = { ...filters, ...(activeFilters || {}) };

      const getAreasFromFilters = () => {
        if (!mergedFilters) return [];
        const areasFromFilters = mergedFilters.areas || mergedFilters.area || [];
        if (Array.isArray(areasFromFilters)) {
          return areasFromFilters.filter((area) => area && area.trim().length > 0);
        }
        if (typeof areasFromFilters === "string") {
          return areasFromFilters.split(",").map((area) => area.trim()).filter((area) => area.length > 0);
        }
        return [];
      };

      const selectedAreas = getAreasFromFilters();

      // City is a scope that must ALWAYS be sent in the payload. When no explicit
      // filter city is set (e.g. first load, before "Search Properties" is clicked),
      // fall back to the sidebar selection / app-wide selected city (localStorage).
      const resolvedCity =
        mergedFilters?.city ||
        localFilters?.city ||
        localStorage.getItem("selectedCity") ||
        "";
      // console.log(filters?.sizeRange?.sqFt?.max, "size range filter value");
      // console.log(filters?.maxsqFt,"max price filter value");

      if (routeType === "broker" || routeType === "builder") {
        endpoint = `/cjidnvij/ceksfbuebijn/user/brokerproperties/filters?page=${page}&size=25`;

        // Filter property types for builder route to only Residential and Commercial
        let filteredType = selectedType;
        if (routeType === "builder") {
          // If selectedType is not Residential or Commercial, default to empty string (all types)
          if (
            selectedType !== "Residential" &&
            selectedType !== "Commercial" &&
            selectedType !== "Residential Rent" &&
            selectedType !== "Commercial Rent" &&
            selectedType !== "Residential Sell" &&
            selectedType !== "Commercial Sell"
          ) {
            filteredType = "";
          }
        }

        // ✅ ENHANCED: Try different area parameter formats
        payload = {
          brokerId: userId || "",
          listedBy: routeType,
          type: filteredType === "All" ? "" : filteredType,

          // Try multiple area parameter names
          location: selectedAreas.length > 0 ? selectedAreas[0] : "",

          ...(filters?.subType && filters.subType.length > 0
            ? {
              unitType: Array.isArray(filters.subType)
                ? filters.subType
                : [filters.subType],
            }
            : activeFilters?.subType && activeFilters.subType.length > 0
              ? {
                unitType: Array.isArray(activeFilters.subType)
                  ? activeFilters.subType
                  : [activeFilters.subType],
              }
              : {}),

          occupencyType:
            filters?.livingTypeFilter && filters.livingTypeFilter.length > 0
              ? filters.livingTypeFilter.split("|")
              : filters?.occupencyType && filters.occupencyType.length > 0
                ? Array.isArray(filters.occupencyType)
                  ? filters.occupencyType
                  : [filters.occupencyType]
                : [],

          bhks: mergedFilters?.bhks || [],
          furnishedTypes: mergedFilters?.furnishedTypes || [],
          propertyCurrentStatus: selectedStatus || "Available",
          verifyStatus: mergedFilters?.verifyStatus || "verified",
          city: resolvedCity,

          page,
          size: 25,
          ...(mergedFilters?.priceMin ? { priceMin: Number(mergedFilters.priceMin) } : {}),
          ...(mergedFilters?.priceMax ? { priceMax: Number(mergedFilters.priceMax) } : {}),

          age: mergedFilters?.age || "",
          propertyAge: mergedFilters?.propertyAge || [],
          bathrooms: mergedFilters?.bathrooms || "",
          sqftMin: mergedFilters?.minsqFt || "",
          sqftMax: mergedFilters?.maxsqFt || "",
          search: premises.length > 0 ? premises.join("|") : "",
        };
      } else if (routeType === "requirement") {
        endpoint = `/cjidnvij/ceksfbuebijn/user/brokerrequirements/filters`;
        const minSqFt = Number(
          filters?.sizeRange?.sqFt?.min ??
          filters?.minsqFt ??
          filters?.minSqft ??
          0
        );
        const maxSqFt = Number(
          filters?.sizeRange?.sqFt?.max ??
          filters?.maxsqFt ??
          filters?.maxSqft ??
          0
        );

        payload = {
          type: selectedType === "All" ? "" : selectedType,
          listedBy: "requirement",
          city: resolvedCity,
          subType: activeFilters?.subType || "",
          areas: selectedAreas,
          page,
          size: 25,
          priceRange: {
            min: filters?.priceRange?.min
              ? Number(normalizePrice(filters?.priceRange?.min))
              : filters?.priceRange?.min,

            max: filters?.priceRange?.max
              ? Number(normalizePrice(filters?.priceRange?.max))
              : filters?.priceRange?.max,
          },
          propertyAge: mergedFilters?.propertyAge || [],
          bhks: mergedFilters?.bhks || [],
          furnishingPreferences:
            mergedFilters?.furnishingPreferences && mergedFilters.furnishingPreferences.length > 0
              ? mergedFilters.furnishingPreferences
              : mergedFilters?.furnishedTypes || [],
          requirementStatus: "Active",
          sizeRange: {
            sqFt: { min: minSqFt, max: maxSqFt },
            sqYd: { min: 0, max: 0 },
          },
          search: premises.length > 0 ? premises.join(" ") : "",
        };
      } else {
        // ✅ Owner properties
        endpoint = `/cjidnvij/ceksfbuebijn/user/v2/properties/filter/jkdbxcb/wdjkwbshuvcw/fhwjvshudcknsb?page=${page}&size=25`;
        payload = {
          areas: selectedAreas,
          area: selectedAreas.length > 0 ? selectedAreas[0] : "",
          city: resolvedCity,
          bhks: mergedFilters?.bhks || [],
          furnishedTypes: mergedFilters?.furnishedTypes || [],
          floors: mergedFilters?.floors || [],
          listedBy: selectedListedBy || "",
          listedOn: selectedListedOn ? formatDate(selectedListedOn) : "",
          maxRent: mergedFilters?.maxRent || "",
          maxsqFt: mergedFilters?.maxsqFt || "",
          minRent: mergedFilters?.minRent || "",
          minsqFt: mergedFilters?.minsqFt || "",
          subType: mergedFilters?.subType || activeFilters?.subType || [],
          userId: userId || "",
          status: selectedStatus || "",
          search: premises.length > 0 ? premises.join("|") : "",
          type: selectedType === "All" ? "" : selectedType,
          propertyAge: mergedFilters?.propertyAge || [],
          livingTypeFilter: mergedFilters?.livingTypeFilter || "",
          verifiedOnly: mergedFilters?.verifiedOnly || false,
        };
      }

      // Merge date filters into the payload for the backend
      if (Object.keys(datePart).length > 0) {
        payload = { ...payload, ...datePart };
      }
      // console.log("🚀 FINAL API REQUEST:", {
      //   endpoint: endpoint,
      //   areasInPayload: payload.areas || payload.area,
      //   fullPayload: payload
      // });

      // API call
      const response = await apiClient("post", endpoint, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response?.success === false) {
        const apiError = response.error || {};
        const errorMessage = getApiErrorMessage(response);

        if (apiError?.code === "DEMO_PAGE_LIMIT_EXCEEDED" && typeof apiError.maxPages === "number") {
          setTotalPages(apiError.maxPages);
        }

        toast.error(errorMessage);
        return false;
      }

      // console.log("📡 API RESPONSE DATA:", response.data);

      const responseData = response.data || {};

      if (responseData?.success === false) {
        const errorMessage = getApiErrorMessage(responseData);

        if (responseData?.code === "DEMO_PAGE_LIMIT_EXCEEDED" && typeof responseData.maxPages === "number") {
          setTotalPages(responseData.maxPages);
        }

        toast.error(errorMessage);
        return false;
      }

      const data = responseData.data || responseData;

      // ✅ CLIENT-SIDE FILTERING FALLBACK
      // If backend doesn't filter by area, we'll do it on the client side
      let filteredProperties = [];

      if (routeType === "broker" || routeType === "builder") {
        const rawProperties = Array.isArray(data) ? data : [];

        // console.log("🔍 Client-side filtering:", {
        //   totalProperties: rawProperties.length,
        //   selectedAreas: selectedAreas,
        //   propertiesAreas: rawProperties.map(p => p.area)
        // });

        // Filter by area on client side if backend didn't
        // if (selectedAreas.length > 0) {
        //   filteredProperties = rawProperties.filter(property => {
        //     const propertyArea = (property.area || "").toLowerCase().trim();
        //     return selectedAreas.some(selectedArea =>
        //       propertyArea.includes(selectedArea.toLowerCase()) ||
        //       selectedArea.toLowerCase().includes(propertyArea)
        //     );
        //   });

        //   // console.log("✅ Client-side filtered properties:", {
        //   //   before: rawProperties.length,
        //   //   after: filteredProperties.length,
        //   //   matchedProperties: filteredProperties.map(p => ({ area: p.area, title: p.title }))
        //   // });
        // } else {
        //   filteredProperties = rawProperties;
        // }

        setProperties(rawProperties);
        setTotalPages(response.data.totalPages || 1);
        setCurrentPage(page);
        // Fetch totalItems from API response for broker/builder route
        setTotalItems(
          response.data.totalItems ||
          response.data.total ||
          filteredProperties.length ||
          0
        );
      } else if (routeType === "requirement") {
        // Similar client-side filtering for requirements
        const rawRequirements = Array.isArray(data?.requirements)
          ? data.requirements
          : Array.isArray(data)
            ? data
            : [];

        if (selectedAreas.length > 0) {
          filteredProperties = rawRequirements.filter((req) => {
            const reqArea = (req.area || "").toLowerCase().trim();
            return selectedAreas.some(
              (selectedArea) =>
                reqArea.includes(selectedArea.toLowerCase()) ||
                selectedArea.toLowerCase().includes(reqArea)
            );
          });
        } else {
          filteredProperties = rawRequirements;
        }

        // Search filtering (Premise Name / Title)
        if (premises && premises.length > 0) {
          filteredProperties = filteredProperties.filter((req) => {
            const reqTitle = (req.title || req.premiseName || "").toLowerCase();
            return premises.some((p) =>
              reqTitle.includes(p.toLowerCase().trim())
            );
          });
        }

        setProperties(filteredProperties);
        setTotalPages(response.data.totalPages || 1);
        setCurrentPage(page);
        // Fetch totalItems from API response for requirement route
        setTotalItems(
          response.data.totalItems ||
          data.totalItems ||
          data.total ||
          filteredProperties.length ||
          0
        );
      } else {
        // Owner properties - use backend filtering
        setProperties(data.properties || []);
        setTotalPages(data.totalPages || 1);
        setCurrentPage(page);
        setTotalItems(data.totalItems || data.properties?.length || 0);
      }

      return true;
    } catch (error) {
      console.error("❌ Error fetching data:", error);
      if (error.response) {
        console.error("❌ Error response:", error.response.data);
      }
      toast.error(getApiErrorMessage(error));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  //   const fetchProperties = async (page = 0, premises = [], selectedType = type) => {
  //     setIsLoading(true);
  //     try {
  //       const userId = Cookies.get("userId");

  //       // Create the base payload
  //       const payload = {
  //         "areas"
  //           :
  //           [],
  //         "bhks"
  //           :
  //           [],
  //         "furnishedTypes"
  //           :
  //           [],
  //         "listedBy"
  //           :
  //           "",
  //         "listedOn"
  //           :
  //           "",
  //         "maxRent"
  //           :
  //           "",
  //         "maxsqFt"
  //           :
  //           10000,
  //         "minRent"
  //           :
  //           "",
  //         "minsqFt"
  //           :
  //           0,
  //         "subtype": [],
  //         userId: userId || "",
  //         status: selectedStatus || "",
  //         search: premises.length > 0 ? premises.join("|") : "",
  //         type: selectedType === "All" ? "" : selectedType,

  //       };

  //       // Add date filtering if dates are selected
  //       if (selectedListedOn) {
  //         const startDate = formatDate(selectedListedOn);
  //         payload.startDate = startDate;

  //         // Only include endDate if it's set and different from startDate
  //         if (selectedEndDate && selectedEndDate.getTime() !== selectedListedOn.getTime()) {
  //           const endDate = formatDate(selectedEndDate);
  //           payload.endDate = endDate;
  //         }

  //         // console.log("Date filter payload:", {
  //         //   startDate: payload.startDate,
  //         //   endDate: payload.endDate,
  //         //   dateRange: dateRange,
  //         //   hasEndDate: !!selectedEndDate
  //         // });
  //       }

  //       // Add any additional filters if they exist
  //       if (filters) {
  //         Object.keys(filters).forEach(key => {
  //           if (key !== 'type') {
  //             // Send livingTypeFilter directly to backend
  //             if (key === 'livingTypeFilter') {
  //               if (filters[key] && filters[key] !== "") {
  //                 payload.livingTypeFilter = filters[key]; // This is already a string like "Family-Bachelors|Only For Family"
  //                 setFamilyFilterActive(true); // ✅ Set family filter as active
  //               }
  //             } else if (key === 'familyFilter' || key === 'familyOnly') {
  //               // Skip these fields as they're not needed in backend
  //               // console.log(`Skipping ${key} field as it's not needed in backend`);
  //             } else {
  //               payload[key] = filters[key];
  //             }
  //             // console.log(`Adding to payload: ${key} = ${payload[key]}`)
  //           }
  //         });

  //         // Reset family filter active state if no livingTypeFilter
  //         if (!filters.livingTypeFilter || filters.livingTypeFilter === "") {
  //           setFamilyFilterActive(false);
  //         }
  //       }

  //       // console.log("Full API payload:", payload);
  //       // console.log("Filters object:", filters);
  //       // console.log("livingTypeFilter value:", filters?.livingTypeFilter);

  //       // Validate payload before sending
  //       const cleanPayload = { ...payload };
  //       delete cleanPayload.familyFilter; // Remove any duplicate fields
  //       delete cleanPayload.familyOnly;   // Remove any duplicate fields

  //       // console.log("Clean API payload:", cleanPayload);

  //            const response = await apiClient(
  //       "post",
  //       `/cjidnvij/ceksfbuebijn/user/v2/properties/filter/jkdbxcb/wdjkwbshuvcw/fhwjvshudcknsb?page=${page}&size=25`,
  //       cleanPayload
  //     );

  //       // const response = await axios.post(
  //       //   `${process.env.REACT_APP_API_IP}/cjidnvij/ceksfbuebijn/user/v2/properties/filter/jkdbxcb/wdjkwbshuvcw/fhwjvshudcknsb?page=${page}&size=25`,
  //       //   payload
  //       // );

  //       const newProperties = response.data.data.properties;
  //       setProperties(newProperties);
  //       setTotalPages(response.data.data.totalPages);
  //       setCurrentPage(page);
  //       setTotalItems(response.data.data.totalItems);

  //       // console.log("This is Page paloyed", payload)
  //       // console.log("This is Page response", response.data.data.properties)

  // //       console.log("API Response:", response.data);

  //     } catch (error) {
  //       // console.error("Error fetching properties:", error);
  //       if (error.response) {
  //         // console.error("Error response:", error.response.data);
  //       }
  //     }
  //     setIsLoading(false);
  //   };

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = `0${d.getMonth() + 1}`.slice(-2); // Ensures two digits
    const day = `0${d.getDate()}`.slice(-2); // Ensures two digits
    const formattedDate = `${year}-${month}-${day}`; // Returns YYYY-MM-DD format

    // console.log("Date formatting:", {
    //   originalDate: date,
    //   dateObject: d,
    //   formatted: formattedDate,
    //   localYear: d.getFullYear(),
    //   localMonth: d.getMonth() + 1,
    //   localDay: d.getDate()
    // });

    return formattedDate;
  };

  const getApiErrorMessage = (response) => {
    const errorSource = response?.error || response?.data || response;

    if (!errorSource) {
      return "Failed to fetch properties.";
    }

    if (typeof errorSource === "string") {
      return errorSource;
    }

    return (
      errorSource.message ||
      errorSource.error?.message ||
      errorSource.error ||
      "Failed to fetch properties."
    );
  };

  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages) {
      setIsPageChanging(true); // Show loader or indicator
      // Split current search query by comma and trim
      const premises = searchQuery
        .split(",")
        .map((premise) => premise.trim())
        .filter((premise) => premise.length > 0);
      fetchProperties(page, premises).then((didSucceed) => {
        if (!didSucceed) {
          setIsPageChanging(false);
          return;
        }
        setIsPageChanging(false); // Hide loader or indicator
      });
    }
  };

  const handleClick = () => {
    navigate("/filter")
  };

  const handleLocalFilterToggle = (name, value) => {
    setLocalFilters((prev) => {
      const current = Array.isArray(prev[name]) ? prev[name] : [];
      const updated = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [name]: updated };
    });
  };

  const handleLocalFilterInput = (e) => {
    const { name, value } = e.target;
    setLocalFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyLocalFilters = () => {
    // Set activeFilters state — this triggers the useEffect dep to re-fetch
    setActiveFilters({ ...localFilters });
    setIsFilterPanelOpen(false);
  };

  const handleClearLocalFilters = () => {
    setLocalFilters({
      bhks: [],
      minRent: "",
      maxRent: "",
      minsqFt: "",
      maxsqFt: "",
      furnishedTypes: [],
      areas: [],
      subType: [],
      city: localStorage.getItem("selectedCity") || "Ahmedabad West",
    });
    setAreaSearchQuery("");
    setSubTypeSearchQuery("");
    setActiveFilters(null); // Reset active filters so that properties are refreshed
  };

  // ✅ Comprehensive clear: resets the side-panel filters AND the filters that
  // were applied from the FilterPage (which come in via location.state).
  const handleClearAllFilters = () => {
    // Reset side-panel (local) filters (keep the city scope)
    const keepCity = localStorage.getItem("selectedCity") || "Ahmedabad West";
    setLocalFilters({
      bhks: [],
      minRent: "",
      maxRent: "",
      minsqFt: "",
      maxsqFt: "",
      furnishedTypes: [],
      areas: [],
      subType: [],
      city: keepCity,
    });
    setAreaSearchQuery("");
    setSubTypeSearchQuery("");
    setActiveFilters(null);

    // Reset other applied constraints
    setSelectedListedOn(null);
    setSelectedEndDate(null);
    setDateRange("");
    setSearchQuery("");
    setSelectedStatus("");
    setSelectedListedBy("");
    setCurrentPage(0);

    // Clear the FilterPage-applied filters persisted in localStorage
    localStorage.removeItem("filters");

    // Clear the filters coming from location.state by navigating with empty state
    // (keep the city scope so results stay within the selected city)
    navigate(location.pathname, {
      replace: true,
      state: { type, filters: { city: keepCity }, routeType },
    });
  };

  // Areas are stored per city and per moduleType, so load the list for the city
  // in scope and the section the user arrived on (owner / broker / builder /
  // requirement) rather than searching the server on every keystroke.
  useEffect(() => {
    if (!localFilters.city) return;

    let cancelled = false;

    const loadAreas = async () => {
      setIsFetchingAreas(true);
      const names = await fetchAreas(localFilters.city, getModuleType());

      if (cancelled) return; // A newer city/section was picked while in flight

      setAreaOptions(names);
      setIsFetchingAreas(false);
    };

    loadAreas();

    return () => {
      cancelled = true;
    };
    // getModuleType() is derived from routeType
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFilters.city, routeType]);

  const handleAreaSearchChange = (e) => {
    setAreaSearchQuery(e.target.value);
  };

  const handleAreaSelect = (area) => {
    if (!localFilters.areas.includes(area)) {
      setLocalFilters((prev) => ({ ...prev, areas: [...prev.areas, area] }));
    }
    setAreaSearchQuery("");
  };

  const handleAreaRemove = (area) => {
    setLocalFilters((prev) => ({
      ...prev,
      areas: prev.areas.filter((a) => a !== area),
    }));
  };

  // Areas matching what has been typed, minus the ones already picked
  const matchingAreas = areaOptions.filter(
    (area) =>
      area.toLowerCase().includes(areaSearchQuery.trim().toLowerCase()) &&
      !localFilters.areas.includes(area)
  );

  const handleSubTypeSearchChange = (e) => {
    setSubTypeSearchQuery(e.target.value);
  };

  const handleSubTypeSelect = (subType) => {
    const subTypeName = typeof subType === "string" ? subType : subType.name || subType.subTypeName || subType;
    if (!localFilters.subType.includes(subTypeName)) {
      setLocalFilters((prev) => ({ ...prev, subType: [...prev.subType, subTypeName] }));
    }
    setSubTypeSearchQuery("");
  };

  const handleSubTypeRemove = (subType) => {
    setLocalFilters((prev) => ({
      ...prev,
      subType: prev.subType.filter((s) => s !== subType),
    }));
  };
  // Create a combined effect to handle all filter-related property fetching
  useEffect(() => {
    // Ensure we have 'type' before calling
    if (!type) return;

    // Use a small timeout or check if we're still initializing to prevent 
    // multiple rapid calls when status/filters are being synchronized from state
    const timer = setTimeout(() => {
      fetchProperties(currentPage);
    }, 50);

    return () => clearTimeout(timer);
  }, [type, filters, activeFilters, selectedStatus, selectedListedOn, selectedEndDate]);

  useEffect(() => {
    if (status && status !== selectedStatus) {
      setSelectedStatus(status);
    }
  }, [status]);

  useEffect(() => {
    if (stateDate && stateDate !== selectedListedOn) {
      setSelectedListedOn(stateDate);
    }
    if (stateEndDate && stateEndDate !== selectedEndDate) {
      setSelectedEndDate(stateEndDate);
    }
    if (stateDateRange && stateDateRange !== dateRange) {
      setDateRange(stateDateRange);
    }
  }, [stateDate, stateEndDate, stateDateRange]);

  // Sync location.state.filters with local side-panel filters
  useEffect(() => {
    if (filters) {
      setLocalFilters((prev) => ({
        ...prev,
        bhks: Array.isArray(filters.bhks) ? filters.bhks : prev.bhks,
        minRent: filters.minRent || filters.priceMin || prev.minRent,
        maxRent: filters.maxRent || filters.priceMax || prev.maxRent,
        minsqFt: filters.minsqFt || filters.sqftMin || prev.minsqFt,
        maxsqFt: filters.maxsqFt || filters.sqftMax || prev.maxsqFt,
        furnishedTypes: Array.isArray(filters.furnishedTypes) ? filters.furnishedTypes : prev.furnishedTypes,
        areas: Array.isArray(filters.areas) ? filters.areas : (filters.area ? (Array.isArray(filters.area) ? filters.area : [filters.area]) : prev.areas),
        subType: Array.isArray(filters.subType) ? filters.subType : (filters.subType ? (Array.isArray(filters.subType) ? filters.subType : [filters.subType]) : prev.subType),
        city: filters.city || prev.city,
      }));
      setActiveFilters(filters);
    }
  }, [filters]);

  useEffect(() => {
    // Only scroll to top when page changes, not on property updates
    if (isPageChanging) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [isPageChanging]);

  useEffect(() => {
    localStorage.setItem("propertyListView", isListView ? "list" : "grid");
  }, [isListView]);

  // Add this state for broker/builder/requirement property types
  const [brokerBuilderRequirementTypes] = useState([
    "All",
    "Residential Rent",
    "Commercial Rent",
    "Residential Sell",
    "Commercial Sell",
  ]);

  // Modify handleTypeChange function
  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setType(newType);
    setCurrentPage(0);

    // Merge current state filters and active sidebar filters to preserve them
    const currentFilters = { ...(filters || {}), ...(activeFilters || {}) };

    if (!(routeType === "broker" || routeType === "builder" || routeType === "requirement")) {
      navigate(`/residential-rental/${newType}`, {
        state: {
          ...location.state,
          type: newType,
          filters: currentFilters,
          selectedListedOn,
          selectedEndDate,
          dateRange
        }
      });
    }
  };

  // Update the select onChange handler
  // const handleTypeChange = (e) => {
  //   const newType = e.target.value;
  //   setType(newType);

  //   // Update location state with new type
  //   navigate(".", {
  //     state: {
  //       ...location.state,
  //       type: newType
  //     },
  //     replace: true
  //   });

  //   // Reset page and fetch properties with new type
  //   setCurrentPage(0);
  //   setIsLoading(true);
  //   fetchProperties(0, [], newType);
  // };

  // First, add the getDateRange function if it's not already there
  const getDateRange = () => {
    const todayDate = new Date();

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(todayDate.getDate() - 30);

    const fourtyDaysAgo = new Date();
    fourtyDaysAgo.setDate(todayDate.getDate() - 40);

    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(todayDate.getDate() - 90);

    switch (type) {
      case "Residential Rent":
        return { minDate: thirtyDaysAgo, maxDate: todayDate };

      case "Commercial Rent":
        return { minDate: fourtyDaysAgo, maxDate: todayDate };

      case "Residential Sell":
      case "Commercial Sell":
        return { minDate: ninetyDaysAgo, maxDate: todayDate };

      default:
        return { minDate: null, maxDate: todayDate };
    }
  };


  return (
    <div className="property-list mx-0 pagination-container relative hide-scrollbar">
      {isPageChanging && <Loader />}

      {/* ── Filter content (shared between sidebar + mobile drawer) ── */}
      {(() => {
        const filterBody = (
          <div className="flex flex-col pl-6 pr-4">
            {/* Header */}
            <div className="flex items-center justify-between py-3 pt-6 border-b">
              <span className="text-base font-bold text-gray-900 flex items-center gap-2">
                <SlidersHorizontal size={16} className="text-[#503691]" />
                Filters
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleClearAllFilters}
                  className="text-xs font-semibold text-[#503691] hover:underline"
                >Clear All</button>
                <button
                  onClick={() => setIsFilterSidebarVisible(false)}
                  className="p-1 hover:bg-purple-50 rounded-full text-[#503691] transition-colors hidden md:block"
                  title="Hide Filters"
                >
                  <ChevronLeft size={20} />
                </button>
              </div>
            </div>

            {/* Applied chips */}
            {/* {(localFilters.bhks.length > 0 || localFilters.furnishedTypes.length > 0 || localFilters.areas.length > 0) && (
              <div className="px-4 pt-3 pb-1 flex flex-wrap gap-1.5">
                {localFilters.bhks.map(b => (
                  <span key={b} className="flex items-center gap-1 bg-purple-50 border border-[#503691] text-[#503691] text-[11px] px-2 py-0.5 rounded-full">
                    {b}
                    <button onClick={() => handleLocalFilterToggle("bhks", b)} className="text-[#503691] hover:text-red-400">×</button>
                  </span>
                ))}
                {localFilters.furnishedTypes.map(f => (
                  <span key={f} className="flex items-center gap-1 bg-purple-50 border border-[#503691] text-[#503691] text-[11px] px-2 py-0.5 rounded-full">
                    {f}
                    <button onClick={() => handleLocalFilterToggle("furnishedTypes", f)} className="text-[#503691] hover:text-red-400">×</button>
                  </span>
                ))}
                {localFilters.areas.map(a => (
                  <span key={a} className="flex items-center gap-1 bg-purple-50 border border-[#503691] text-[#503691] text-[11px] px-2 py-0.5 rounded-full">
                    {a}
                    <button onClick={() => handleAreaRemove(a)} className="text-[#503691] hover:text-red-400">×</button>
                  </span>
                ))}
              </div>
            )} */}

            {/* Scrollable body */}
            <div className="py-3 space-y-2.5">
              {/* City */}
              <div>
                <div className="text-sm font-bold text-gray-800 mb-2">City</div>
                <div className="flex flex-wrap gap-1.5">
                  {cityOptions.map((city) => {
                    const cityAllowed = hasCityAccess(city);
                    return (
                      <button
                        key={city}
                        onClick={() => cityAllowed && handleCitySelect(city)}
                        disabled={!cityAllowed}
                        title={!cityAllowed ? "No access to this city" : undefined}
                        className={`px-2 py-1 rounded-full text-[11px] font-medium border transition-all ${
                          localFilters.city === city
                            ? "bg-[#503691] text-white border-[#503691]"
                            : "bg-white text-gray-600 border-gray-300 hover:border-[#503691]"
                        } ${!cityAllowed ? "opacity-50 cursor-not-allowed hover:border-gray-300" : ""}`}
                      >
                        {city}
                      </button>
                    );
                  })}
                </div>
              </div>

              <hr />

              {/* Area */}
              <div>
                <button
                  className="flex w-full items-center justify-between text-sm font-bold text-gray-800 mb-2"
                  onClick={() => setAreaExpanded(p => !p)}
                >
                  Area
                  {areaExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                {areaExpanded && (
                  <div className="space-y-2">
                    {/* Selected area chips */}
                    {localFilters.areas.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-1">
                        {localFilters.areas.map(area => (
                          <span
                            key={area}
                            className="flex items-center gap-1 bg-purple-50 border border-[#503691] text-[#503691] text-[11px] px-2 py-0.5 rounded-full"
                          >
                            {area}
                            <button
                              onClick={() => handleAreaRemove(area)}
                              className="text-[#503691] hover:text-red-400 leading-none"
                            >×</button>
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Search input */}
                    <div className="relative" ref={areaSearchRef}>
                      <input
                        type="text"
                        value={areaSearchQuery}
                        onChange={handleAreaSearchChange}
                        placeholder="Search area..."
                        className="w-full px-2 py-1.5 text-xs rounded-lg border border-gray-200 focus:border-[#503691] focus:outline-none"
                      />
                      {isFetchingAreas && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2">
                          <div className="w-3 h-3 border-2 border-[#503691] border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                      {/* Suggestions dropdown */}
                      {areaSearchQuery && matchingAreas.length > 0 && (
                        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                          {matchingAreas.map((area, idx) => (
                            <button
                              key={idx}
                              onClick={() => handleAreaSelect(area)}
                              className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-purple-50 hover:text-[#503691] transition-colors"
                            >
                              {area}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* SubType */}
              <div>
                <button
                  className="flex w-full items-center justify-between text-sm font-bold text-gray-800 mb-2"
                  onClick={() => setSubTypeExpanded(p => !p)}
                >
                  Property Type
                  {subTypeExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                {subTypeExpanded && (
                  <div className="space-y-2">
                    {/* Selected subType chips */}
                    {localFilters.subType.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-1">
                        {localFilters.subType.map(sub => (
                          <span
                            key={sub}
                            className="flex items-center gap-1 bg-purple-50 border border-[#503691] text-[#503691] text-[11px] px-2 py-0.5 rounded-full"
                          >
                            {sub}
                            <button
                              onClick={() => handleSubTypeRemove(sub)}
                              className="text-[#503691] hover:text-red-400 leading-none"
                            >×</button>
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Search input */}
                    <div className="relative">
                      <input
                        type="text"
                        value={subTypeSearchQuery}
                        onChange={handleSubTypeSearchChange}
                        placeholder="Search property type..."
                        className="w-full px-2 py-1.5 text-xs rounded-lg border border-gray-200 focus:border-[#503691] focus:outline-none"
                      />
                      {/* Suggestions dropdown */}
                      {subTypeSearchQuery && (
                        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                          {subTypes
                            .filter(st => st.toLowerCase().includes(subTypeSearchQuery.toLowerCase()))
                            .filter(st => !localFilters.subType.includes(st))
                            .map((suggestion, idx) => (
                              <button
                                key={idx}
                                onClick={() => handleSubTypeSelect(suggestion)}
                                className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-purple-50 hover:text-[#503691] transition-colors"
                              >
                                {suggestion}
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <hr />

              {/* BHK - Only show for Residential types */}
              {type !== "Commercial Rent" && type !== "Commercial Sell" && (
                <>
                  <div>
                    <button
                      className="flex w-full items-center justify-between text-sm font-bold text-gray-800 mb-2"
                      onClick={() => setBhkExpanded(p => !p)}
                    >
                      BHK Type
                      {bhkExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                    {bhkExpanded && (
                      <div className="flex flex-wrap gap-1.5">
                        {["1 BHK", "2 BHK", "3 BHK", "4 BHK", "5 BHK", "6 BHK", "6+ BHK"].map(bhk => (
                          <button
                            key={bhk}
                            onClick={() => handleLocalFilterToggle("bhks", bhk)}
                            className={`px-2 py-1 rounded-full text-[11px] font-medium border transition-all ${localFilters.bhks.includes(bhk)
                              ? "bg-[#503691] text-white border-[#503691]"
                              : "bg-white text-gray-600 border-gray-300 hover:border-[#503691]"
                              }`}
                          >{bhk}</button>
                        ))}
                      </div>
                    )}
                  </div>

                  <hr />
                </>
              )}

              {/* Budget */}
              <div>
                <button
                  className="flex w-full items-center justify-between text-sm font-bold text-gray-800 mb-2"
                  onClick={() => setBudgetExpanded(p => !p)}
                >
                  Budget
                  {budgetExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                {budgetExpanded && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-1.5">
                      <div>
                        <label className="text-[10px] text-gray-400 uppercase font-medium pl-0.5">Min</label>
                        <input
                          type="text"
                          name="minRent"
                          placeholder="No min"
                          value={localFilters.minRent}
                          onChange={handleLocalFilterInput}
                          className="w-full mt-0.5 px-2 py-1.5 text-xs rounded-lg border border-gray-200 focus:border-[#503691] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-400 uppercase font-medium pl-0.5">Max</label>
                        <input
                          type="text"
                          name="maxRent"
                          placeholder="No max"
                          value={localFilters.maxRent}
                          onChange={handleLocalFilterInput}
                          className="w-full mt-0.5 px-2 py-1.5 text-xs rounded-lg border border-gray-200 focus:border-[#503691] focus:outline-none"
                        />
                      </div>
                    </div>
                    {/* <div className="flex items-center gap-2 text-[10px] text-gray-400">
                      <span>₹0</span>
                      <div className="flex-1 h-1 rounded-full bg-gradient-to-r from-[#503691] to-[#a78bfa]" />
                      <span>₹5.0 Cr</span>
                    </div> */}
                  </div>
                )}
              </div>

              <hr />

              {/* Sq.Ft */}
              <div>
                <button
                  className="flex w-full items-center justify-between text-sm font-bold text-gray-800 mb-2"
                  onClick={() => setSqftExpanded(p => !p)}
                >
                  Size (Sq.Ft)
                  {sqftExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                {sqftExpanded && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-gray-400 uppercase font-medium pl-0.5">Min</label>
                      <input
                        type="text"
                        name="minsqFt"
                        placeholder="Min"
                        value={localFilters.minsqFt}
                        onChange={handleLocalFilterInput}
                        className="w-full mt-0.5 px-2 py-1.5 text-xs rounded-lg border border-gray-200 focus:border-[#503691] focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-400 uppercase font-medium pl-0.5">Max</label>
                      <input
                        type="text"
                        name="maxsqFt"
                        placeholder="Max"
                        value={localFilters.maxsqFt}
                        onChange={handleLocalFilterInput}
                        className="w-full mt-0.5 px-2 py-1.5 text-xs rounded-lg border border-gray-200 focus:border-[#503691] focus:outline-none"
                      />
                    </div>
                  </div>
                )}
              </div>

              <hr />

              {/* Furnished Type */}
              <div>
                <button
                  className="flex w-full items-center justify-between text-sm font-bold text-gray-800 mb-2"
                  onClick={() => setFurnishedExpanded(p => !p)}
                >
                  Furnished Type
                  {furnishedExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                {furnishedExpanded && (
                  <div className="flex flex-wrap gap-1.5">
                    {["Furnished", "Semi-Furnished", "Kitchen-Fix", "Unfurnished"].map(opt => (
                      <button
                        key={opt}
                        onClick={() => handleLocalFilterToggle("furnishedTypes", opt)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${localFilters.furnishedTypes.includes(opt)
                          ? "bg-[#503691] text-white border-[#503691]"
                          : "bg-white text-gray-600 border-gray-300 hover:border-[#503691]"
                          }`}
                      >{opt}</button>
                    ))}
                  </div>
                )}
              </div>

              {/* <hr /> */}

            </div>

            {/* Apply button */}
            <div className="py-3 border-t">
              <button
                onClick={handleApplyLocalFilters}
                className="w-full py-2.5 bg-[#503691] text-white rounded-xl font-bold text-sm hover:bg-[#3f2a72] transition-colors"
              >
                Search Properties
              </button>
            </div>
          </div>
        );

        return (
          <>
            <aside className={`hidden md:flex flex-col fixed top-[80px] left-6 bottom-4 w-[260px] bg-white border border-gray-100 shadow-lg z-30 overflow-y-auto rounded-2xl custom-scrollbar transition-all duration-300 ${isFilterSidebarVisible ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0 pointer-events-none"}`}>
              {filterBody}
            </aside>

            {/* Float Toggle Button when sidebar is hidden */}
            {!isFilterSidebarVisible && (
              <button
                onClick={() => setIsFilterSidebarVisible(true)}
                className="hidden md:flex fixed top-[90px] left-2 z-40 bg-white border border-gray-100 shadow-lg p-2 rounded-full text-[#503691] hover:bg-purple-50 transition-all duration-300 animate-in fade-in slide-in-from-left-4"
                title="Show Filters"
              >
                <ChevronRight size={24} />
              </button>
            )}

            {/* Mobile: slide-in drawer */}
            <div className={`fixed inset-0 z-[9999] md:hidden transition-all duration-300 ${isFilterPanelOpen ? "visible" : "invisible"}`}>
              <div
                className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${isFilterPanelOpen ? "opacity-100" : "opacity-0"}`}
                onClick={() => setIsFilterPanelOpen(false)}
              />
              <div className={`absolute left-0 top-0 bottom-0 w-[280px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ${isFilterPanelOpen ? "translate-x-0" : "-translate-x-full"}`}>
                {filterBody}
              </div>
            </div>
          </>
        );
      })()}

      {/* ── Main content (offset by sidebar width + left margin on desktop) ── */}
      <div className={`transition-all duration-300 ${isFilterSidebarVisible ? "md:pl-[290px]" : "md:pl-12"}`}>
        <div className="property-list-container">
          <div className="flex items-center justify-center pt-2 flex-wrap gap-y-3 gap-x-2 my-4 md:mx-3 lg:mx-2 md:my-2 w-full relative z-[60]">
            <div className="flex items-center px-1 sm:px-2 md:px-0 gap-0 order-1">
              <button
                onClick={() => navigate(-1)}
                className="md:hidden p-1 -ml-1 mr-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <ArrowLeft size={24} />
              </button>
              {/* Date picker */}
              <div className="relative z-[1000]">
                <DatePicker
                  selected={selectedListedOn}
                  onChange={handleDateChange}
                  className="form-control h-10 p-2 rounded-l-lg md:w-[140px] w-[110px] text-sm shadow-md"
                  placeholderText="Select date"
                  isClearable={true}
                  clearButtonTitle="Clear date"
                  maxDate={new Date()}
                  minDate={getDateRange().minDate}
                  popperClassName="z-[10001] !left-[30px]"
                  portalId="root"
                />
              </div>
              {/* Date range */}
              <div className="relative">
                <select
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  className="form-control h-10 p-1 md:p-2 rounded-r-lg w-[85px] md:w-[90px] text-sm shadow-md"
                >
                  <option value="">Range</option>
                  <option value="last7days">Last 7 Days</option>
                  <option value="last15days">Last 15 Days</option>
                  <option value="last30days">Last 30 Days</option>
                </select>
                {dateRange === "custom" && (
                  <div className="absolute top-full left-0 mt-1 z-[9999] bg-white rounded-lg">
                    <DatePicker
                      selected={selectedEndDate}
                      onChange={handleEndDateChange}
                      className="form-control h-10 p-2 rounded-lg w-[200px] shadow-md"
                      placeholderText="Select end date"
                      isClearable={true}
                      clearButtonTitle="Clear date"
                      maxDate={new Date()}
                      minDate={selectedListedOn || getDateRange().minDate}
                      popperClassName="z-[10001]"
                      portalId="root"
                    />
                  </div>
                )}
              </div>
            </div>
            {/* Filter button - Next to date range on mobile and desktop */}
            <div className="flex items-center order-2 md:order-3">
              <button
                className="bg-[#503691] text-white py-3 h-10 flex items-center gap-1 px-2.5 flex-row rounded-xl shadow-md"
                onClick={handleClick}
              >
                <SlidersHorizontal size={16} />
                <span className="text-xs md:text-sm whitespace-nowrap">All Filter</span>
              </button>
            </div>

            {/* Property Type + Search box - Full width on mobile, alongside others on desktop */}
            <div className="flex flex-row rounded-lg order-3 md:order-2 w-full md:w-auto px-1 sm:px-2 md:px-0">
              <select
                value={type}
                onChange={handleTypeChange}
                className="rounded-l-lg p-2 h-10 shadow-md text-[15px] min-w-[60px] md:w-[50px] lg:w-[160px]"
              >
                {(routeType === "broker" || routeType === "builder" || routeType === "requirement"
                  ? brokerBuilderRequirementTypes
                  : propertyTypes
                ).map(propertyType => (
                  <option key={propertyType} value={propertyType}>
                    {isMobile && propertyType === "Residential Rent" ? "RR"
                      : isMobile && propertyType === "Commercial Rent" ? "CR"
                        : isMobile && propertyType === "Residential Sell" ? "RS"
                          : isMobile && propertyType === "Commercial Sell" ? "CS"
                            : propertyType}
                  </option>
                ))}
              </select>

              <div className="relative w-full sm:w-[300px] md:w-[350px] lg:w-[150px] xl:w-[450px]">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => {
                    setSearchQuery(e.target.value);
                    if (!e.target.value.trim()) handleSearch();
                  }}
                  onKeyDown={e => {
                    if (e.key === "Enter") handleSearch();
                  }}
                  className="p-3 pr-14 md:pr-16 rounded-r-lg w-full h-10 placeholder:text-gray-400 shadow-md text-gray-700"
                  placeholder="Enter premise names"
                />
                <button
                  onClick={() => { handleSearch(); }}
                  className="absolute right-1 md:right-2 rounded-lg top-1 bottom-1 px-3 md:px-4 bg-[#503691] hover:bg-[#3f2a72] text-white"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Grid/List toggle - Order 4 */}
            <div className="hidden md:flex items-center gap-2 order-4">
              <div className="flex bg-white border-2 gap-2 w-[75px] h-10 border-blue-100 rounded-full py-[5px] px-2">
                <button
                  className={`h-6 w-6 flex items-center justify-center rounded-full transition-all ${!isListView ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-sm" : "text-blue-600"}`}
                  onClick={() => setIsListView(false)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="34" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-layout-grid h-4 w-4">
                    <rect width="7" height="7" x="3" y="3" rx="1"></rect>
                    <rect width="7" height="7" x="14" y="3" rx="1"></rect>
                    <rect width="7" height="7" x="14" y="14" rx="1"></rect>
                    <rect width="7" height="7" x="3" y="14" rx="1"></rect>
                  </svg>
                </button>
                <button
                  className={`flex h-6 w-6 items-center justify-center rounded-full transition-all ${isListView ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-sm" : "text-blue-600"}`}
                  onClick={() => setIsListView(true)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-list h-4 w-4">
                    <line x1="8" x2="21" y1="6" y2="6" />
                    <line x1="8" x2="21" y1="12" y2="12" />
                    <line x1="8" x2="21" y1="18" y2="18" />
                    <line x1="3" x2="3.01" y1="6" y2="6" />
                    <line x1="3" x2="3.01" y1="12" y2="12" />
                    <line x1="3" x2="3.01" y1="18" y2="18" />
                  </svg>
                </button>
              </div>

              <button
                type="button"
                onClick={toggleFullscreen}
                title={isFullscreen ? "Exit full screen" : "Full screen view"}
                className="h-10 w-10 flex items-center justify-center rounded-full bg-white border-2 border-blue-100 text-blue-600 hover:bg-blue-50 transition-all"
              >
                {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Total count */}
          <p className="text-gray-700 pb-2 text-center">
            {totalItems > 0 && `${totalItems} Properties found`}
          </p>

          <div>
            {isListView ? (
              <List
                properties={properties}
                fetchProperties={() => fetchProperties(currentPage)}
              />
            ) : (
              <div className="properties-grid">
                {properties.map((property, index) => (
                  <div key={property.id || index} className="gap-4 mt-2 md:m-4">
                    {routeType === "builder" ? (
                      <NewBuilderCard {...property} />
                    ) : (
                      <PropertyCard
                        property={property}
                        fetchProperties={() => fetchProperties(currentPage)}
                        contactInfoMap={contactInfoMap}
                        setContactInfoMap={setContactInfoMap}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {isLoading && properties.length === 0 ? (
            <Loader />
          ) : properties.length === 0 ? (
            <div className="no-properties flex flex-col items-center justify-center text-center gap-4 py-16 min-h-[50vh] w-full">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="56"
                height="56"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#503691"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
                <path d="M8 11h6" />
              </svg>
              <div>
                <p className="text-lg font-semibold text-gray-800">
                  No properties found
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Try adjusting or clearing your filters to see more results.
                </p>
              </div>
              <button
                onClick={handleClearAllFilters}
                className="bg-[#503691] hover:bg-[#3f2a74] text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors shadow-md"
              >
                Clear Filter
              </button>
            </div>
          ) : null}

          <div className="pagination">
            <button
              className={`pagination-btn ${currentPage === 0 ? "disabled" : ""}`}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0}
            >Previous</button>
            <span className="pagination-info">Page {currentPage + 1} of {totalPages}</span>
            <button
              className={`pagination-btn ${currentPage === totalPages - 1 ? "disabled" : ""}`}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1}
            >Next</button>
          </div>
        </div>
      </div>

      <SimpleModal
        isOpen={modal.isOpen}
        onClose={() => setModal({ ...modal, isOpen: false })}
        title={modal.title}
      >
        {modal.content}
      </SimpleModal>
    </div>
  );
};

export default ResidentialRental;
